


import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  TextField,
  Select,
  FormControl,
  MenuItem,
  Chip,
  Alert,
  LinearProgress,
} from "@mui/material";
import {
  Add,
  Edit,
  Visibility,
  Print,
  Refresh,
  CheckCircle,
  ErrorOutline,
  Download,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import * as mammoth from "mammoth";
import api from "../../../api/axiosInterceptor";
import { SnackBarComponent } from "../../../Components/snackBar/snackBar";
import { LoadingSpinner } from "../../../Components/spinner/spinner";
import TableComponent from "../../../Components/TableComponent/TableComponent";
import { TODAY, StatusChip, ApprovalTimeline } from "./component/sharedForm";
import ApprovalPanel from "./approvalPanel";
import FEDropdown from "./feDropdown";

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE URL — served via Django backend proxy
// Backend endpoint: GET /v1/api/legal/templates/petition/
// ─────────────────────────────────────────────────────────────────────────────
const TEMPLATE_URL = "/legal/templates/petition/";

// ─────────────────────────────────────────────────────────────────────────────
// PLACEHOLDER DEFINITIONS — every {{key}} used in petition_template.docx
// ─────────────────────────────────────────────────────────────────────────────
export const PETITION_PLACEHOLDERS = {
  misc_no: { label: "Misc No.", type: "text" },
  misc_year: { label: "Misc Year", type: "text" },
  fe: { label: "Financial Entity (M/s)", type: "fe-dropdown" },
  ca_name: { label: "CA Name", type: "text" },
  ca_designation: { label: "CA Designation", type: "text" },
  ca_age: { label: "CA Age", type: "text" },
  respondents: { label: "Respondents", type: "textarea" },
  govt_notification_no: { label: "Govt. Notification No.", type: "text" },
  annexure_ref: { label: "Annexure Ref.", type: "text" },
  interim_attachment_dated: { label: "Interim Attachment Dated", type: "date" },
  gazette_dated: { label: "Gazette Dated", type: "date" },
  gazette_copy_dated: { label: "Gazette Copy Dated", type: "text" },
  news_paper_dated: { label: "News Paper Dated", type: "date" },
  news_paper_english: { label: "English News Paper Name", type: "text" },
  news_paper_kannada: { label: "Kannada News Paper Name", type: "text" },
  gazette_affixture_annexure: {
    label: "Gazette Affixture Annexure",
    type: "text",
  },
  respondent_no: { label: "Respondent No.", type: "text" },
  delay_days: { label: "Delay Days", type: "number" },
  schedule_notification_no: {
    label: "Schedule Notification No.",
    type: "text",
  },
  schedule_annexure: { label: "Schedule Annexure", type: "text" },
  schedule_sl_no: { label: "Schedule Sl. No.", type: "text" },
  date: { label: "Date", type: "date" },
};

// ─────────────────────────────────────────────────────────────────────────────
// BLANK DEFAULT
// ─────────────────────────────────────────────────────────────────────────────
const BLANK = Object.fromEntries(
  Object.keys(PETITION_PLACEHOLDERS).map((k) => [k, k === "date" ? TODAY : ""]),
);

// ─────────────────────────────────────────────────────────────────────────────
// STATUS
// ─────────────────────────────────────────────────────────────────────────────
const SL = {
  draft: "Draft",
  submitted: "Submitted",
  legal_review: "Legal Review",
  approved_l1: "Approved (Legal)",
  rejected_l1: "Rejected (Legal)",
  approved_ac: "Approved (AC)",
  rejected_ac: "Rejected (AC)",
  approved_js: "Approved (JS)",
  rejected_js: "Rejected (JS)",
  approved_ca: "Approved (CA/SPLOCA)",
  rejected_ca: "Rejected (CA/SPLOCA)",
  issued: "Issued",
};
const EDITABLE = [
  "draft",
  "rejected_l1",
  "rejected_ac",
  "rejected_js",
  "rejected_ca",
];
const CAN_CREATE = ["legal"];
const EP = "/legal/petition/";

// ─────────────────────────────────────────────────────────────────────────────
// MAMMOTH STYLE MAP — preserves Word paragraph spacing, indents, alignment,
// heading levels, bold/italic/underline, tables, and list structure exactly.
// ─────────────────────────────────────────────────────────────────────────────
const MAMMOTH_OPTIONS = {
  styleMap: [
    // Headings
    "p[style-name='Heading 1'] => h1:fresh",
    "p[style-name='Heading 2'] => h2:fresh",
    "p[style-name='Heading 3'] => h3:fresh",
    "p[style-name='Heading 4'] => h4:fresh",
    "p[style-name='Heading 5'] => h5:fresh",
    "p[style-name='Heading 6'] => h6:fresh",

    // Body / Normal text — preserve as paragraph
    "p[style-name='Normal'] => p:fresh",
    "p[style-name='Body Text'] => p:fresh",
    "p[style-name='Body Text 2'] => p:fresh",
    "p[style-name='Body Text 3'] => p:fresh",

    // Centered / Right aligned
    "p[style-name='centered'] => p.doc-centered:fresh",
    "p[style-name='Center'] => p.doc-centered:fresh",

    // Lists
    "p[style-name='List Paragraph'] => p.doc-list-para:fresh",
    "p[style-name='List Number'] => ol > li:fresh",
    "p[style-name='List Bullet'] => ul > li:fresh",

    // Table caption / title
    "p[style-name='Caption'] => p.doc-caption:fresh",
    "p[style-name='Table Caption'] => p.doc-caption:fresh",

    // Run-level character styles
    "r[style-name='Strong'] => strong",
    "r[style-name='Emphasis'] => em",
    "r[style-name='Underline'] => u",

    // Preserve tab stops as a visible tab span
    "t => span.doc-tab",
  ],
  includeDefaultStyleMap: true,

  // Preserve empty paragraphs as <p><br></p> so blank lines show correctly
  ignoreEmptyParagraphs: false,

  // Convert images to inline base64 so they appear in the HTML preview
  convertImage: mammoth.images.imgElement((image) =>
    image.read("base64").then((imageBuffer) => ({
      src: `data:${image.contentType};base64,${imageBuffer}`,
    })),
  ),
};

// ─────────────────────────────────────────────────────────────────────────────
// HOOK — fetch raw .docx ArrayBuffer once, then convert to HTML with mammoth.
// Also stores the raw ArrayBuffer so we can print the original file.
// ─────────────────────────────────────────────────────────────────────────────
const useFetchTemplate = (proxyUrl) => {
  const [templateHtml, setTemplateHtml] = useState(null);
  const [rawBuffer, setRawBuffer] = useState(null); // ← kept for print
  const [detectedKeys, setDetectedKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!proxyUrl) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setTemplateHtml(null);
    setRawBuffer(null);
    setDetectedKeys([]);

    (async () => {
      try {
        const res = await api.get(proxyUrl, { responseType: "arraybuffer" });
        const arrayBuffer = res.data;

        // Convert with full style-map so structure is preserved
        const result = await mammoth.convertToHtml(
          { arrayBuffer },
          MAMMOTH_OPTIONS,
        );
        const html = result.value;
        const keys = [
          ...new Set([...html.matchAll(/\{\{(\w+)\}\}/g)].map((m) => m[1])),
        ];

        if (!cancelled) {
          setTemplateHtml(html);
          setRawBuffer(arrayBuffer); // store original bytes
          setDetectedKeys(keys);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.data?.error ||
              e?.message ||
              "Unknown error loading template",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [proxyUrl]);

  return { templateHtml, rawBuffer, detectedKeys, loading, error };
};

// ─────────────────────────────────────────────────────────────────────────────
// XML ESCAPE
// ─────────────────────────────────────────────────────────────────────────────
const escapeXml = (str) =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

// ─────────────────────────────────────────────────────────────────────────────
// COLLAPSE SPLIT RUNS IN DOCX XML
//
// Word's XML editor (and autocorrect) routinely splits a single logical token
// like {{misc_no}} across several <w:r><w:t>…</w:t></w:r> runs inside the
// same paragraph <w:p>.  Strategy:
//
//   1. For every <w:p>…</w:p> block, extract the plain text of ALL <w:t> nodes.
//   2. If the concatenated text contains {{key}}, rebuild the paragraph so that
//      run whose <w:t> contains the placeholder carries the substituted value,
//      and all the "fragment" runs that were part of the split are removed.
//
// This works regardless of how many runs the placeholder was split across,
// what rPr (formatting) they had, or whether xml:space="preserve" was used.
// ─────────────────────────────────────────────────────────────────────────────
const collapseAndReplace = (xml, substitutions) => {
  // Process paragraph by paragraph so we never accidentally merge runs across
  // paragraph boundaries.
  return xml.replace(/<w:p[ >][\s\S]*?<\/w:p>/g, (para) => {
    // Collect all <w:t> text values in order, noting which run each belongs to
    // A "run" here means one <w:r>…</w:r> block (may contain rPr + w:t).
    // We rebuild a map: runIndex → { fullRunXml, tText }
    const runPattern = /<w:r[ >][\s\S]*?<\/w:r>/g;
    const runs = [];
    let m;
    while ((m = runPattern.exec(para)) !== null) {
      // Extract the text inside <w:t>…</w:t> (may have attributes like xml:space)
      const tMatch = m[0].match(/<w:t(?:[^>]*)>([\s\S]*?)<\/w:t>/);
      runs.push({
        xml: m[0],
        text: tMatch ? tMatch[1] : "",
        index: m.index,
      });
    }

    if (!runs.length) return para;

    // Concatenate all run texts to get the paragraph's plain text
    const fullText = runs.map((r) => r.text).join("");

    // Check if any substitution key appears in the full text
    let hasMatch = false;
    for (const key of Object.keys(substitutions)) {
      if (fullText.includes(`{{${key}}}`)) {
        hasMatch = true;
        break;
      }
    }
    if (!hasMatch) return para;

    // Apply all substitutions to the concatenated text
    let substituted = fullText;
    for (const [key, val] of Object.entries(substitutions)) {
      substituted = substituted.split(`{{${key}}}`).join(escapeXml(val));
    }

    // Strategy: keep the FIRST run's formatting (rPr) and replace its w:t with
    // the entire substituted text; remove all subsequent runs that contributed
    // to the placeholder.  Runs that had no part in any placeholder are kept.
    //
    // We do this character-by-character: walk runs, accumulate text, once we
    // detect we've consumed a full placeholder swap it.
    //
    // Simpler reliable approach: replace the entire run section of the paragraph
    // with one merged run using the first run's rPr and the substituted text.
    //
    // Extract everything outside the runs (paragraph properties <w:pPr> etc.)
    // so we don't lose paragraph formatting.
    const firstRun = runs[0];
    const rPrMatch = firstRun.xml.match(/<w:rPr>[\s\S]*?<\/w:rPr>/);
    const rPr = rPrMatch ? rPrMatch[0] : "";
    const mergedRunXml = `<w:r>${rPr}<w:t xml:space="preserve">${substituted}</w:t></w:r>`;

    // Replace ALL runs in the paragraph with the single merged run
    // We rebuild the paragraph: keep everything before the first run, insert
    // merged run, keep everything after the last run.
    const firstRunStart = para.indexOf(runs[0].xml);
    const lastRun = runs[runs.length - 1];
    const lastRunEnd = para.indexOf(lastRun.xml) + lastRun.xml.length;

    return para.slice(0, firstRunStart) + mergedRunXml + para.slice(lastRunEnd);
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// INJECT FILLED VALUES INTO DOCX BYTES
// ─────────────────────────────────────────────────────────────────────────────
const injectIntoDocx = async (arrayBuffer, formData) => {
  try {
    const JSZip = (await import("jszip")).default;
    const zip = await JSZip.loadAsync(arrayBuffer);

    // Build substitution map: key → display value (unescaped — escapeXml applied inside)
    const substitutions = {};
    Object.keys(PETITION_PLACEHOLDERS).forEach((key) => {
      substitutions[key] =
        key === "fe" ? formData._fe_display || "" : formData[key] || "";
    });

    // Patch document body + any headers/footers that exist in the zip
    const filesToPatch = [
      "word/document.xml",
      ...Object.keys(zip.files).filter((f) =>
        /^word\/(header|footer)\d*\.xml$/.test(f),
      ),
    ];

    for (const fileName of filesToPatch) {
      const file = zip.file(fileName);
      if (!file) continue;

      let xml = await file.async("string");

      // Pass 1: collapse split runs & substitute
      xml = collapseAndReplace(xml, substitutions);

      // Pass 2: catch any remaining single-run placeholders that Pass 1 missed
      // (e.g. placeholder was already in one run — collapseAndReplace handles
      // these too, but a simple replaceAll is a cheap safety net)
      Object.entries(substitutions).forEach(([key, val]) => {
        xml = xml.split(`{{${key}}}`).join(escapeXml(val));
      });

      zip.file(fileName, xml);
    }

    return await zip.generateAsync({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      compression: "DEFLATE",
      compressionOptions: { level: 6 },
    });
  } catch (err) {
    console.error("injectIntoDocx failed:", err);
    // Fallback: return unfilled original
    return new Blob([arrayBuffer], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// PRINT ORIGINAL WORD FILE
// Injects values into the docx, creates an object URL, opens a hidden iframe
// that triggers the browser's native "Open / Save" dialog so the user can
// open it in Word and use Word's own print dialog — giving pixel-perfect output.
// ─────────────────────────────────────────────────────────────────────────────
const printOriginalDocx = async (rawBuffer, formData) => {
  const blob = await injectIntoDocx(rawBuffer, formData);
  const url = URL.createObjectURL(blob);

  // Trigger download so the user opens it in Word and prints from there.
  // This is the only way to guarantee the original Word formatting is preserved.
  const a = document.createElement("a");
  a.href = url;
  a.download = `petition_${formData.misc_no || "draft"}_${formData.misc_year || ""}.docx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  // Revoke after a short delay
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

// ─────────────────────────────────────────────────────────────────────────────
// CSS for the Word-faithful HTML preview
// Mirrors typical Word page: A4 width, Times New Roman, justified text,
// proper paragraph spacing, tab stops, centred headings, table borders.
// ─────────────────────────────────────────────────────────────────────────────
const PREVIEW_STYLES = `
  .word-preview-root {
    font-family: 'Times New Roman', Times, serif;
    font-size: 12pt;
    line-height: 1.5;
    color: #000;
    background: #fff;
    padding: 72pt 90pt;          /* ~1 inch margins */
    max-width: 816px;            /* A4 ≈ 8.5 in @ 96 dpi */
    margin: 0 auto;
    box-shadow: 0 0 0 1px #ddd;
    min-height: 1123px;          /* A4 height */
    text-align: justify;
  }

  /* Headings — centred, bold, no extra space */
  .word-preview-root h1,
  .word-preview-root h2,
  .word-preview-root h3,
  .word-preview-root h4,
  .word-preview-root h5,
  .word-preview-root h6 {
    text-align: center;
    font-family: 'Times New Roman', Times, serif;
    font-weight: bold;
    margin: 0 0 6pt 0;
    line-height: 1.4;
  }
  .word-preview-root h1 { font-size: 14pt; }
  .word-preview-root h2 { font-size: 13pt; }
  .word-preview-root h3 { font-size: 12pt; }

  /* Normal paragraphs */
  .word-preview-root p {
    margin: 0 0 6pt 0;
    text-align: justify;
  }

  /* Blank paragraph = blank line — do NOT collapse */
  .word-preview-root p:empty::after,
  .word-preview-root p br:only-child {
    content: '\\00a0';
    display: block;
  }

  /* Centred paragraphs (mapped from Word "Center" style) */
  .word-preview-root p.doc-centered { text-align: center; }

  /* List paragraph indentation */
  .word-preview-root p.doc-list-para { margin-left: 36pt; }

  /* Ordered / unordered lists */
  .word-preview-root ol,
  .word-preview-root ul { margin: 0 0 6pt 36pt; padding: 0; }
  .word-preview-root li { margin-bottom: 3pt; }

  /* Captions */
  .word-preview-root p.doc-caption { text-align: center; font-size: 10pt; color: #444; }

  /* Inline tab → mimic Word tab stop as a fixed-width space */
  .word-preview-root span.doc-tab { display: inline-block; width: 36pt; }

  /* Tables */
  .word-preview-root table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 6pt;
    font-size: 11pt;
  }
  .word-preview-root td,
  .word-preview-root th {
    border: 1px solid #000;
    padding: 4pt 6pt;
    vertical-align: top;
  }
  .word-preview-root th {
    font-weight: bold;
    background: #f2f2f2;
  }

  /* Inline character styles */
  .word-preview-root strong { font-weight: bold; }
  .word-preview-root em     { font-style: italic; }
  .word-preview-root u      { text-decoration: underline; }

  /* Filled placeholder chip — green */
  .word-preview-root .ph-filled {
    display: inline-block;
    background: #e8f5e9;
    border: 1px solid #66bb6a;
    border-radius: 3px;
    padding: 0 6px;
    font-size: 11px;
    color: #1b5e20;
    font-weight: 700;
    vertical-align: middle;
    font-family: Arial, sans-serif;
    line-height: 1.6;
  }

  /* Empty placeholder chip — orange */
  .word-preview-root .ph-empty {
    display: inline-block;
    background: #fff3e0;
    border: 1px dashed #ff9800;
    border-radius: 3px;
    padding: 0 6px;
    font-size: 11px;
    color: #e65100;
    vertical-align: middle;
    font-family: Arial, sans-serif;
    line-height: 1.6;
  }

  /* Read-only empty placeholder */
  .word-preview-root .ph-missing {
    color: #bbb;
    font-style: italic;
    font-family: Arial, sans-serif;
    font-size: 11px;
  }

  @media print {
    .word-preview-root {
      box-shadow: none;
      padding: 0;
      max-width: 100%;
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE PREVIEW — replaces {{keys}} with live values or chips
// fe key → reads _fe_display (name_of_fe) not formData.fe (id)
// ─────────────────────────────────────────────────────────────────────────────
const TemplatePreview = ({ html, formData, readOnly }) => {
  const rendered = html.replace(/\{\{(\w+)\}\}/g, (_m, key) => {
    const val = key === "fe" ? formData._fe_display || "" : formData[key] || "";

    if (!val) {
      if (readOnly) {
        return `<span class="ph-missing">[${key}]</span>`;
      }
      return `<span class="ph-empty">{{${key}}}</span>`;
    }
    return `<span class="ph-filled">${val}</span>`;
  });

  return (
    <>
      <style>{PREVIEW_STYLES}</style>
      <div
        className="word-preview-root"
        dangerouslySetInnerHTML={{ __html: rendered }}
      />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE ERROR
// ─────────────────────────────────────────────────────────────────────────────
const TemplateError = ({ error }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      p: 6,
      gap: 2,
      background: "#fff9f9",
      border: "1px dashed #f44336",
      borderRadius: 2,
      minHeight: 300,
    }}
  >
    <ErrorOutline sx={{ fontSize: 48, color: "error.main" }} />
    <Typography variant="h6" color="error.main" fontWeight="bold">
      Template Failed to Load
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ textAlign: "center", maxWidth: 420 }}
    >
      {error}
    </Typography>
    <Alert severity="info" sx={{ mt: 1, width: "100%", maxWidth: 480 }}>
      <Typography variant="body2">
        Backend proxy endpoint: <code>{TEMPLATE_URL}</code>
        <br />
        Add <code>"petition"</code> to <code>TEMPLATE_S3_KEYS</code> in{" "}
        <code>template_proxy_view.py</code> and ensure{" "}
        <code>petition_template.docx</code> is uploaded to S3.
      </Typography>
    </Alert>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE LOADING
// ─────────────────────────────────────────────────────────────────────────────
const TemplateLoading = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      p: 6,
      gap: 2,
      minHeight: 300,
    }}
  >
    <CircularProgress size={32} />
    <Typography variant="body2" color="text.secondary">
      Loading Word template…
    </Typography>
    <LinearProgress sx={{ width: 200, mt: 1 }} />
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// PLACEHOLDER FIELD
// ─────────────────────────────────────────────────────────────────────────────
const PlaceholderField = ({
  fieldKey,
  config,
  formData,
  onChange,
  onFESelect,
}) => {
  const value = formData[fieldKey] || "";
  const isFilled = fieldKey === "fe" ? !!formData._fe_display : !!value;

  const label = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
      <code
        style={{
          background: "#f5f5f5",
          padding: "1px 5px",
          borderRadius: 3,
          fontSize: 11,
        }}
      >
        {`{{${fieldKey}}}`}
      </code>
      <Typography variant="caption" color="text.secondary">
        — {config.label}
      </Typography>
      {isFilled && <CheckCircle sx={{ fontSize: 13, color: "success.main" }} />}
    </Box>
  );

  if (config.type === "fe-dropdown") {
    return (
      <Box sx={{ mb: 2 }}>
        {label}
        <FEDropdown
          value={formData.fe}
          disabled={false}
          showAddress={true}
          onSelect={(fe) => onFESelect(fe)}
        />
        {formData._fe_display && (
          <Typography
            variant="caption"
            sx={{ color: "success.dark", mt: 0.4, display: "block" }}
          >
            ✓ <strong>{formData._fe_display}</strong>
          </Typography>
        )}
      </Box>
    );
  }

  if (config.type === "textarea") {
    return (
      <Box sx={{ mb: 2 }}>
        {label}
        <TextField
          value={value}
          onChange={(e) => onChange(fieldKey, e.target.value)}
          multiline
          rows={3}
          fullWidth
          size="small"
          InputProps={{ style: { fontSize: 13 } }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      {label}
      <TextField
        type={config.type || "text"}
        value={value}
        onChange={(e) => onChange(fieldKey, e.target.value)}
        fullWidth
        size="small"
        InputProps={{ style: { fontSize: 13 } }}
        InputLabelProps={["date"].includes(config.type) ? { shrink: true } : {}}
      />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FIELDS PANEL — sticky left column
// ─────────────────────────────────────────────────────────────────────────────
const FieldsPanel = ({ formData, onChange, onFESelect, detectedKeys }) => {
  const keysToRender = detectedKeys?.length
    ? detectedKeys
    : Object.keys(PETITION_PLACEHOLDERS);

  const filledCount = keysToRender.filter((k) =>
    k === "fe" ? !!formData._fe_display : !!formData[k],
  ).length;

  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        p: 2,
        position: "sticky",
        top: 0,
        maxHeight: "calc(100vh - 180px)",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          Placeholders
        </Typography>
        <Chip
          size="small"
          label={`${filledCount} / ${keysToRender.length} filled`}
          color={filledCount === keysToRender.length ? "success" : "default"}
          sx={{ fontSize: 11 }}
        />
      </Box>

      {keysToRender.map((key) => {
        const config = PETITION_PLACEHOLDERS[key] || {
          label: key,
          type: "text",
        };
        return (
          <PlaceholderField
            key={key}
            fieldKey={key}
            config={config}
            formData={formData}
            onChange={onChange}
            onFESelect={onFESelect}
          />
        );
      })}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export const PetitionFormComponent = () => {
  const role = useSelector((s) => s.auth.role);

  const [view, setView] = useState("list");
  const [list, setList] = useState([]);
  const [listLoading, setLL] = useState(false);
  const [formData, setFormData] = useState({});
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  // ── FE filter state for the list view
  const [filterFE, setFilterFE] = useState(null);

  // Template auto-fetched via backend proxy on mount
  // rawBuffer is the original .docx bytes — used for faithful print
  const { templateHtml, rawBuffer, detectedKeys, loading, error } =
    useFetchTemplate(TEMPLATE_URL);

  // ── Fetch list
  const fetchList = useCallback(async () => {
    setLL(true);
    try {
      const res = await api.get(EP);
      setList(res.data);
    } catch {
      setSnack({ open: true, message: "Failed to load", severity: "error" });
    } finally {
      setLL(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, []);

  // ── Field helpers
  const set = (f, v) => setFormData((d) => ({ ...d, [f]: v }));

  // FE select: id → API, name_of_fe → template preview
  const handleFESelect = (fe) => {
    if (!fe) return;
    setFormData((d) => ({
      ...d,
      fe: fe.id,
      _fe_display: fe.name_of_fe,
      fe_detail: fe,
    }));
  };

  // ── Navigation
  const openNew = () => {
    setFormData({ ...BLANK });
    setSelected(null);
    setView("form");
  };

  const openEdit = (item) => {
    setFormData({ ...item, _fe_display: item.fe_detail?.name_of_fe || "" });
    setSelected(item);
    setView("form");
  };

  const openView = async (item) => {
    try {
      const res = await api.get(`${EP}${item.id}/`);
      setFormData({
        ...res.data,
        _fe_display: res.data.fe_detail?.name_of_fe || "",
      });
      setSelected(res.data);
    } catch {
      setFormData({ ...item, _fe_display: item.fe_detail?.name_of_fe || "" });
      setSelected(item);
    }
    setView("detail");
  };

  // ── Save — strip display-only fields before API call
  const handleSave = async () => {
    setSaving(true);
    const { fe_detail, _fe_display, ...payload } = formData;
    try {
      if (selected) {
        await api.patch(`${EP}${selected.id}/`, payload);
        setSnack({
          open: true,
          message: "Updated successfully",
          severity: "success",
        });
      } else {
        await api.post(EP, payload);
        setSnack({
          open: true,
          message: "Created successfully",
          severity: "success",
        });
      }
      fetchList();
      setView("list");
    } catch (e) {
      setSnack({
        open: true,
        message: JSON.stringify(e.response?.data) || "Save failed",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Print: download filled .docx → user opens in Word → prints from Word
  // This is the only approach that guarantees 100% original Word formatting.
  const handlePrint = async () => {
    if (!rawBuffer) {
      setSnack({
        open: true,
        message: "Template not loaded yet",
        severity: "warning",
      });
      return;
    }
    setPrinting(true);
    try {
      await printOriginalDocx(rawBuffer, formData);
      setSnack({
        open: true,
        message:
          "Filled .docx downloaded — open in Word and use File → Print for exact formatting.",
        severity: "info",
      });
    } catch (e) {
      setSnack({
        open: true,
        message: `Print error: ${e.message}`,
        severity: "error",
      });
    } finally {
      setPrinting(false);
    }
  };

  // ── Table columns
  const tableCols = [
    { id: "slNo", field: "slNo", headerName: "Sl No.", width: 70 },
    { id: "misc_no", field: "misc_no", headerName: "Misc No.", width: 110 },
    { id: "misc_year", field: "misc_year", headerName: "Year", width: 90 },
    { id: "fe_name", field: "fe_name", headerName: "CA For (M/s)", width: 280 },
    { id: "date", field: "date", headerName: "Date", width: 120 },
    {
      id: "respondent_no",
      field: "respondent_no",
      headerName: "Respondent No.",
      width: 140,
    },
    {
      id: "statusLabel",
      field: "statusLabel",
      headerName: "Status",
      width: 180,
    },
    {
      id: "actions",
      field: "actions",
      headerName: "Actions",
      width: 110,
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="View">
            <IconButton size="small" onClick={() => openView(p.row)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {EDITABLE.includes(p.row.status) && CAN_CREATE.includes(role) && (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={() => openEdit(p.row)}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const tableRows = list.map((item, i) => ({
    ...item,
    slNo: i + 1,
    statusLabel: SL[item.status] || item.status,
    fe_name: item.fe_detail?.name_of_fe || "",
  }));

  // ── Filtered rows for list view — only show when FE is selected
  const filteredTableRows = filterFE
    ? tableRows.filter(
        (r) => r.fe === filterFE.id || r.fe_detail?.id === filterFE.id,
      )
    : [];

  // ── Document preview renderer
  const renderDocPreview = (readOnly = false) => {
    if (loading) return <TemplateLoading />;
    if (error) return <TemplateError error={error} />;
    if (templateHtml)
      return (
        <TemplatePreview
          html={templateHtml}
          formData={formData}
          readOnly={readOnly}
        />
      );
    return null;
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        width: "100%",
        mt: 2,
        borderRadius: 2,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        backgroundColor: "#fff",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid #e0e0e0",
          background: "rgba(25,118,210,0.04)",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Petition Management
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Flow: Legal → AC → Joint Secretary → CA / SPLOCA
        </Typography>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* ── LIST ────────────────────────────────────────────────────────── */}
        {view === "list" && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  Petition List
                </Typography>
                {loading && (
                  <Chip
                    size="small"
                    label="Loading template…"
                    variant="outlined"
                    sx={{ fontSize: 11 }}
                  />
                )}
                {templateHtml && !loading && (
                  <Chip
                    size="small"
                    label="Template ready"
                    color="success"
                    sx={{ fontSize: 11 }}
                  />
                )}
                {error && (
                  <Chip
                    size="small"
                    icon={<ErrorOutline sx={{ fontSize: 14 }} />}
                    label="Template error"
                    color="error"
                    sx={{ fontSize: 11 }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Refresh">
                  <IconButton onClick={fetchList}>
                    <Refresh />
                  </IconButton>
                </Tooltip>
                {CAN_CREATE.includes(role) && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={openNew}
                  >
                    Add New Petition
                  </Button>
                )}
              </Box>
            </Box>

            {/* FE Filter */}
            <Box sx={{ mb: 2, maxWidth: 400 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={0.5}
              >
                Select Financial Entity to view records
              </Typography>
              <FEDropdown
                value={filterFE?.id || null}
                disabled={false}
                showAddress={true}
                onSelect={(fe) => setFilterFE(fe || null)}
              />
            </Box>

            {!filterFE ? (
              <Box
                sx={{
                  p: 4,
                  textAlign: "center",
                  border: "1px dashed #bbb",
                  borderRadius: 2,
                  background: "#fafafa",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Please select a Financial Entity above to view records.
                </Typography>
              </Box>
            ) : listLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <LoadingSpinner size={30} />
              </Box>
            ) : (
              <TableComponent
                columns={tableCols}
                rows={filteredTableRows}
                manageColumnStatus
                enableSort
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    fontWeight: "bold",
                  },
                }}
              />
            )}
          </>
        )}

        {/* ── FORM ────────────────────────────────────────────────────────── */}
        {view === "form" && (
          <>
            <Button size="small" onClick={() => setView("list")} sx={{ mb: 2 }}>
              ← Back to List
            </Button>
            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "360px 1fr",
                gap: 2,
                alignItems: "start",
              }}
            >
              {/* LEFT — placeholder inputs (only when template loaded) */}
              {templateHtml && (
                <FieldsPanel
                  formData={formData}
                  onChange={set}
                  onFESelect={handleFESelect}
                  detectedKeys={detectedKeys}
                />
              )}

              {/* RIGHT — live document preview (Word-faithful) */}
              <Box
                sx={{
                  background: "#f0f0f0" /* grey mat around the white page */,
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  p: 2,
                  minHeight: 600,
                  overflowX: "auto",
                  gridColumn: templateHtml ? "auto" : "1 / -1",
                }}
              >
                {templateHtml && (
                  <Box
                    sx={{
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="bold"
                    >
                      Live Preview (Word-faithful)
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        background: "#e8f5e9",
                        border: "1px solid #66bb6a",
                        borderRadius: "3px",
                        px: 0.5,
                        fontSize: 11,
                        color: "#1b5e20",
                      }}
                    >
                      filled
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      /
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        background: "#fff3e0",
                        border: "1px dashed #ff9800",
                        borderRadius: "3px",
                        px: 0.5,
                        fontSize: 11,
                        color: "#e65100",
                      }}
                    >
                      {"{{empty}}"}
                    </Box>
                  </Box>
                )}
                {renderDocPreview(false)}
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2, mt: 3, alignItems: "center" }}>
              <Button variant="outlined" onClick={() => setView("list")}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !templateHtml}
                startIcon={saving ? <CircularProgress size={16} /> : null}
              >
                {selected ? "Update" : "Save Draft"}
              </Button>
              <Tooltip title="Downloads a filled .docx — open in Microsoft Word and print for exact formatting">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={
                      printing ? <CircularProgress size={16} /> : <Download />
                    }
                    onClick={handlePrint}
                    disabled={printing || !rawBuffer}
                  >
                    {printing ? "Preparing…" : "Download & Print (Word)"}
                  </Button>
                </span>
              </Tooltip>
              {!templateHtml && !loading && (
                <Typography variant="caption" color="error">
                  Template must load successfully before saving.
                </Typography>
              )}
            </Box>
          </>
        )}

        {/* ── DETAIL ──────────────────────────────────────────────────────── */}
        {view === "detail" && selected && (
          <>
            <Button size="small" onClick={() => setView("list")} sx={{ mb: 2 }}>
              ← Back to List
            </Button>

            <Box
              sx={{
                textAlign: "center",
                mb: 2,
                p: 2,
                background: "#f5f5f5",
                borderRadius: 1,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                IN THE COURT OF SPECIAL JUDGE, SPECIAL COURT FOR KPIDFE CASES,
                BENGALURU (CCH - 92)
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Misc No. {selected.misc_no} / {selected.misc_year}
              </Typography>
              {/* <Box sx={{ mt: 1 }}>
                <StatusChip status={selected.status} />
              </Box> */}
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                background: "#f0f0f0",
                border: "1px solid #ddd",
                borderRadius: 2,
                p: 2,
                mb: 2,
                overflowX: "auto",
              }}
            >
              {renderDocPreview(true)}
            </Box>

            <ApprovalTimeline form={selected} flowType="petition" />
            <ApprovalPanel
              form={selected}
              apiPath={EP}
              role={role}
              flowType="petition"
              onRefresh={() => openView(selected)}
            />

            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button variant="outlined" onClick={() => setView("list")}>
                Back to List
              </Button>
              <Tooltip title="Downloads a filled .docx — open in Microsoft Word and print for exact formatting">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={
                      printing ? <CircularProgress size={16} /> : <Download />
                    }
                    onClick={handlePrint}
                    disabled={printing || !rawBuffer}
                  >
                    {printing ? "Preparing…" : "Download & Print (Word)"}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </>
        )}
      </Box>

      <SnackBarComponent
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
};